import { motion } from "framer-motion";
import { HelpCircle, RefreshCw } from "lucide-react";
import type { MemberProfile } from "../types";
import { formatTierLabel, isMissingTier } from "../mapVerifiedMember";

interface MemberHeaderProps {
  member: MemberProfile;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export function MemberHeader({
  member,
  onRefresh,
  isRefreshing = false,
}: MemberHeaderProps) {
  const tierMissing = isMissingTier(member.tier);
  const tierLabel = formatTierLabel(member.tier);

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm"
    >
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-amber-950 px-6 py-8 sm:px-8 sm:py-10">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(251,191,36,0.18),_transparent_55%)]" />
        <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-300 to-amber-500 text-2xl font-bold text-slate-900 shadow-lg shadow-amber-500/30">
              {member.avatarInitials}
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">
                  {member.name}
                </h1>
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-amber-400/20 px-2.5 py-0.5 text-xs font-semibold text-amber-200 ring-1 ring-amber-300/40"
                  title={tierMissing ? "Tier not available" : tierLabel}
                  aria-label={
                    tierMissing ? "Tier not available" : `Tier ${tierLabel}`
                  }
                >
                  {tierMissing ? (
                    <HelpCircle className="h-3.5 w-3.5" aria-hidden />
                  ) : (
                    tierLabel
                  )}
                </span>
                <span className="inline-flex items-center rounded-full bg-emerald-400/15 px-2.5 py-0.5 text-xs font-semibold text-emerald-300 ring-1 ring-emerald-400/30">
                  {member.status}
                </span>
              </div>
              <p className="mt-2 font-mono text-sm text-slate-300">
                Membership ID · {member.memberId}
              </p>
              <p className="mt-1 text-sm text-slate-400">
                Joined Since {member.joinedSince}
              </p>
            </div>
          </div>

          {onRefresh ? (
            <button
              type="button"
              onClick={onRefresh}
              disabled={isRefreshing}
              className="inline-flex items-center justify-center gap-2 self-start rounded-xl border border-amber-300/30 bg-amber-400/15 px-3.5 py-2 text-xs font-semibold text-amber-100 transition hover:bg-amber-400/25 disabled:cursor-not-allowed disabled:opacity-60 sm:self-center"
              aria-label="Refresh member details"
              title="Refresh"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${isRefreshing ? "animate-spin" : ""}`}
                aria-hidden
              />
              {isRefreshing ? "Refreshing…" : "Refresh"}
            </button>
          ) : null}
        </div>
      </div>
    </motion.section>
  );
}
